import React from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import TeamLogo from "@/components/TeamLogo";
import { ChevronUp, ChevronDown, Minus } from "lucide-react";

const StandingsTab = ({ standings, teamMap }) => {
  return (
    <div className="space-y-3" data-testid="standings-table-wrap">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display uppercase tracking-tight text-xl">
          League Table
        </h2>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Top 4 qualify
        </span>
      </div>

      <Card className="bg-card border-border rounded-md overflow-hidden p-0">
        <div className="grid grid-cols-12 gap-1 px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border bg-[#0A0D0A]">
          <div className="col-span-1">#</div>
          <div className="col-span-4 sm:col-span-5">Team</div>
          <div className="col-span-1 text-center">M</div>
          <div className="col-span-1 text-center">W</div>
          <div className="col-span-1 text-center hidden sm:block">L</div>
          <div className="col-span-1 text-center hidden sm:block">NR</div>
          <div className="col-span-2 sm:col-span-1 text-center">Pts</div>
          <div className="col-span-2 text-right pr-1">NRR</div>
        </div>

        {standings.rows.map((row) => {
          const team = teamMap[row.short];
          const inTop4 = row.pos <= 4;
          return (
            <div
              key={row.short}
              className={`grid grid-cols-12 gap-1 items-center px-3 py-2.5 border-b border-border/50 last:border-b-0 ${
                inTop4 ? "bg-primary/[0.04]" : ""
              }`}
              data-testid={`standings-row-${row.short}`}
            >
              <div className="col-span-1 num text-base text-foreground flex items-center gap-1">
                {row.pos}
                {inTop4 && (
                  <span className="hidden sm:inline-block w-1 h-1 rounded-full bg-primary" />
                )}
              </div>
              <div className="col-span-4 sm:col-span-5 flex items-center gap-2 min-w-0">
                <TeamLogo team={team} size={26} />
                <div className="min-w-0">
                  <div className="font-display uppercase text-sm leading-tight truncate">
                    {team?.short}
                  </div>
                  <div className="text-[10px] text-muted-foreground truncate hidden sm:block">
                    {team?.name}
                  </div>
                </div>
              </div>
              <div className="col-span-1 text-center num text-sm">{row.M}</div>
              <div className="col-span-1 text-center num text-sm text-primary">{row.W}</div>
              <div className="col-span-1 text-center num text-sm hidden sm:block text-destructive">
                {row.L}
              </div>
              <div className="col-span-1 text-center num text-sm hidden sm:block text-muted-foreground">
                {row.NR}
              </div>
              <div className="col-span-2 sm:col-span-1 text-center num text-base font-semibold">
                {row.Pts}
              </div>
              <div className="col-span-2 text-right pr-1 num text-sm">
                <NrrChip nrr={row.NRR} />
              </div>
            </div>
          );
        })}
      </Card>

      <div className="text-[11px] text-muted-foreground leading-relaxed pt-1">
        NRR = (runs scored ÷ overs faced) − (runs conceded ÷ overs bowled). Tie-broken by NRR
        when points are level. Top 4 advance to the playoffs.
      </div>
    </div>
  );
};

const NrrChip = ({ nrr }) => {
  if (nrr === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-muted-foreground">
        <Minus className="w-3 h-3" /> 0.000
      </span>
    );
  }
  const positive = nrr > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 ${
        positive ? "text-primary" : "text-destructive"
      }`}
    >
      {positive ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      {Math.abs(nrr).toFixed(3)}
    </span>
  );
};

export default StandingsTab;
